import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, BadgeCheck } from "lucide-react";

const lines = ["SEU PRÓXIMO", "CARRO", "COMEÇA AQUI."];

const metrics = [
  { value: "R$ 6M", label: "em vendas gerenciadas" },
  { value: "27", label: "portais integrados" },
  { value: "IA 24/7", label: "pré-atendimento ativo" },
  { value: "98%", label: "de satisfação" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden grain" data-testid="hero-section">
      <motion.div className="absolute inset-0 bg-[#090A0F]" style={{ y: imgY, scale: imgScale }}>
        <div className="absolute right-[-10%] bottom-[-20%] w-[75%] h-[85%] rounded-full bg-emerald-500/20 blur-[130px]" />
        <div className="absolute left-[-10%] top-[-20%] w-[45%] h-[60%] rounded-full bg-emerald-900/20 blur-[110px]" />
        <div className="absolute bottom-[24%] sm:bottom-[4%] right-[2%] w-[88%] sm:w-[60%] lg:w-[52%] h-[26px] rounded-[50%] bg-emerald-400/25 blur-2xl" />
        <img
          src="/hero-audi-cut.png"
          alt="Hatch do estoque Seed Motors"
          className="absolute bottom-[26%] sm:bottom-[6%] right-0 w-[92%] sm:w-[62%] lg:w-[54%] opacity-45 sm:opacity-100 object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.7)]"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#090A0F] via-[#090A0F]/45 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#090A0F] to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-40 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-8"
          data-testid="hero-badge"
        >
          <BadgeCheck className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-mono2 tracking-widest uppercase text-emerald-300">
            Ecossistema automotivo completo
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-5xl sm:text-7xl lg:text-8xl text-white" data-testid="hero-title">
          {lines.map((line, i) => (
            <span key={line} className="hero-mask-line">
              <motion.span
                className={`block ${i === 1 ? "text-emerald-400 text-glow" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subtitle"
        >
          Estoque Selected, simulação de financiamento em segundos e uma operação
          movida por ERP + CRM com IA. A Seed Motors conecta você ao carro certo,
          sem burocracia.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#estoque"
            data-testid="hero-cta-estoque"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-8 py-4 rounded-full transition-colors duration-300 shadow-[0_0_36px_rgba(16,185,129,0.4)]"
          >
            Ver estoque
            <ArrowDown className="w-4 h-4" />
          </a>
          <a
            href="#simulador"
            data-testid="hero-cta-simulador"
            className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300"
          >
            Simular financiamento
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800/50 rounded-2xl overflow-hidden"
          data-testid="hero-metrics"
        >
          {metrics.map((m) => (
            <div key={m.label} className="bg-[#0d0f17]/90 backdrop-blur px-6 py-5">
              <p className="font-display text-2xl sm:text-3xl font-bold text-emerald-400">{m.value}</p>
              <p className="text-xs text-slate-400 mt-1">{m.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
